import {
  CompanyEntityPayload,
  CompanyPayload,
} from '@/pages/CompanyCenter/store/types';
import {
  CounterPartryExposureRequestPayload,
  GetDebtAndHedgeRequestPayload,
  GetEntityByIdRequestPayload,
  GetEntityCashflowsRequestPayload,
  GetEntityDebtRequestPayload,
  GetEntityDocumentsRequestPayload,
  GetEntityTransactionValueRequestPayload,
  GetEntityTransactionsRequestPayload,
  GetHistoricalEntityTransactionsRequestPayload,
  GetIFRSTradesRequestPayload,
  GetPortfolioTradeValueRequestPayload,
  GetScenarioAnalysisDownloadSuccessPayload,
  GetSponsorsByEntitiesRequestPayload,
} from '@/pages/EntityPortfolio/store/actions';
import { AxiosInstance, AxiosResponse } from 'axios';

export default class CompanyEntityAPI {
  client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async companyTradesWithInfo({
    comp_entity_id,
  }: {
    comp_entity_id: string | number;
  }) {
    return this.client.get(
      `v2/global-filter/company-entities/${comp_entity_id}/transactions-with-info/`,
    );
  }

  async getAllCompanyEntities(
    brieflyWithSponsors: boolean = false,
  ): Promise<AxiosResponse> {
    const params = brieflyWithSponsors ? '?brieflyWithSponsors=true' : '';
    return await this.client.get(`/v2/company/company-entity/${params}`);
  }

  async getAllCompany(): Promise<AxiosResponse> {
    return await this.client.get('/v2/company/get-all-company-list/');
  }

  async getAllGvMappings(): Promise<AxiosResponse> {
    return await this.client.get('/v2/company/get-all-gv-mappings/');
  }

  async getAllCompanyTypes(): Promise<AxiosResponse> {
    return await this.client.get('/v2/company/get-all-company-types/');
  }

  async getAllOwnershipTypes(): Promise<AxiosResponse> {
    return await this.client.get(
      '/v2/company/get-all-company-ownership-types/',
    );
  }

  async getHedgeStandards(): Promise<AxiosResponse> {
    return await this.client.get('/v2/company/get-hedge-standards/');
  }

  async createCompany(data: CompanyPayload): Promise<AxiosResponse> {
    return await this.client.post('/v2/company/company/', data);
  }

  async updateCompany(
    company_id: string,
    data: CompanyPayload,
  ): Promise<AxiosResponse> {
    return await this.client.put(`/v2/company/company/${company_id}/`, data);
  }

  async createCompanyEntity(
    data: CompanyEntityPayload,
  ): Promise<AxiosResponse> {
    return await this.client.post('/v2/company/company-entity/', data);
  }

  async updateCompanyEntity(
    entity_id: string,
    data: CompanyEntityPayload,
  ): Promise<AxiosResponse> {
    return await this.client.put(
      `/v2/company/company-entity/${entity_id}/`,
      data,
    );
  }

  async listEntityTransactions(payload: GetEntityTransactionsRequestPayload) {
    return this.client.post('/v2/hedging/entity/transactions', payload);
  }

  async listHistoricalEntityTransactions(
    payload: GetHistoricalEntityTransactionsRequestPayload,
  ) {
    return this.client.post(
      '/v2/hedging/entity/transactions/historical-transactions',
      payload,
    );
  }

  async listEntityDebts(payload: GetEntityDebtRequestPayload) {
    let url = `/v2/hedging/entity/debt/${payload.company_entity_id}/${payload.valuation_date}/${payload.user_currency_id}/`;
    if (payload.historical_date_filter) {
      url = `${url}?historical_date_filter=${payload.historical_date_filter}`;
    }
    return this.client.get(url);
  }
  async listEntityTradeValues(
    payload: GetEntityTransactionValueRequestPayload,
  ) {
    return this.client.post('/v2/hedging/entity/transactions/values', payload);
  }
  async getEntityById(payload: GetEntityByIdRequestPayload) {
    return this.client.get(`/v2/company/company-entity/${payload.entity_id}/`);
  }

  async getEntityDocuments(payload: GetEntityDocumentsRequestPayload) {
    return this.client.get(
      `/v2/file-management/company-entity/${payload.company_entity_id}/files/`,
    );
  }

  async getEntityCounterpartyExposure(
    payload: CounterPartryExposureRequestPayload,
  ) {
    return this.client.post('v2/dashboard/counterparty-exposure/', payload);
  }

  async getEntityCounterpartyHedgedNotionals(
    payload: GetDebtAndHedgeRequestPayload,
  ) {
    return this.client.post('v2/hedging/hedged-notionals/', payload);
  }

  async getEntityCashFlows(payload: GetEntityCashflowsRequestPayload) {
    return this.client.post('/v2/hedging/entity/cashflows/', payload);
  }

  async getEntityIFRSTrades(payload: GetIFRSTradesRequestPayload) {
    return this.client.post('/v2/reporting/get-ifrs-report-trades/', payload);
  }

  async getSponsorsByEntities(payload: GetSponsorsByEntitiesRequestPayload) {
    return this.client.post(
      '/v2/organizations/company/sponsors-by-entities/',
      payload,
    );
  }

  // Company Relationships
  async createCompanyRelationship(data: {
    owner_company_id: string;
    child_company_id: string;
    start_date: string;
    end_date: string | null;
  }): Promise<AxiosResponse> {
    return await this.client.post(
      '/v2/organizations/company/relationships/',
      data,
    );
  }

  async updateCompanyRelationship(
    relationship_id: string,
    data: {
      start_date: string;
      end_date: string | null;
    },
  ): Promise<AxiosResponse> {
    return await this.client.put(
      `/v2/organizations/company/relationships/${relationship_id}/`,
      data,
    );
  }

  async deleteCompanyRelationship(
    relationship_id: string,
  ): Promise<AxiosResponse> {
    return await this.client.delete(
      `/v2/organizations/company/relationships/${relationship_id}/`,
    );
  }

  // scenario analysis graph api
  async generateScenarioAnalysisGraph(
    payload: GetScenarioAnalysisDownloadSuccessPayload,
  ): Promise<AxiosResponse> {
    return await this.client.post('/v2/hedging/scenario-analysis/', payload);
  }

  // individual trade value for portfolio
  async getPortfolioTradeValue(payload: GetPortfolioTradeValueRequestPayload) {
    return this.client.post(
      '/v2/organizations/entity/trade-aggregated-values/',
      payload,
    );
  }

  async getEntityUsers(payload: {
    entity_ids: string[];
  }): Promise<AxiosResponse> {
    return this.client.post('/v2/organizations/entity/users/', payload);
  }
}
